<template>
  <reduced-layout-fi
    :api-data="undefined"
    :interface-language="undefined"
  >
    <loading-indicator variant="spinnerFullPageCover" />
  </reduced-layout-fi>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { LoadingIndicator } from 'atoms/index';
import { useHandlingFeeCookie } from 'components/handling-fee-limit-banner-fi/handlingFeeCookie';
import ReducedLayoutFi from 'components/reduced-layout-fi/ReducedLayoutFi.vue';
import {
  allowedStateChars,
  isNonceError,
  loginDialogCookies,
  useLogin,
  usernameCookie,
} from 'composables/login';
import { onMounted } from 'vue';

import { useCookies } from '@/composables/cookies';
import { useFischer } from '@/composables/fischerPlugin';
import { markAsNotIDS } from '@/utils/idsTools';

const MERGECARTPromise = () => import('gql/mergeCart.gql');

const props = defineProps<{
  autodetect?: false | string;
}>();

const { clearHandlingFeeLimitCookie } = useHandlingFeeCookie();
const {
  criticalLoginFailure,
  setDevToken,
  initializeAuthorization,
  isSpecialPage,
  logDebug,
  getCookies,
  setCookies,
  postAccessTokenInSession,
  notifyLogin,
} = useLogin();
const fischer = useFischer();
const { cookies: vueCookies } = useCookies();

defineOptions({
  name: 'SiteLoginStandalone',
});

const emit = defineEmits<{
  'logged-in': [
    {
      redirect: string;
      accessToken: string;
      data?: string;
    },
  ];
}>();

const mergeCarts = async (
  accessToken: string,
  idToken: string,
  cartId: string,
): Promise<boolean | null> => {
  try {
    if (!cartId || cartId === 'current') {
      logDebug('not merging cart, cartId', cartId);
      return null;
    }
    logDebug('started merging carts...');
    const MERGECART = (await MERGECARTPromise()).default;
    const { mutate } = useMutation(MERGECART);
    await mutate({
      variables: {
        cartId,
        accessToken,
        idToken,
        baseSiteId: fischer.baseSiteId(),
      },
      onError: (error: any) => console.error('Could not merge carts', error),
    });
    logDebug('finished merging carts');
    return true;
  } catch (error) {
    console.error('Could not merge cart', error, cartId);
    return false;
  }
};

const restoreTokenState = async (stateId: string) => {
  const cookies = getCookies();
  const state = cookies[`state-${stateId}`];
  delete cookies[`state-${stateId}`]; // it is important to remove these to protect against replays, even if we fail later

  const { value: nonce, validUntil: nonceValidUntil, data } = cookies[`nonce-${stateId}`] || {};
  delete cookies[`nonce-${stateId}`];

  if (nonceValidUntil) {
    // re-set nonce with 'used' flag, so we can see this 'was' a valid login and can re-attempt
    cookies[`nonce-${stateId}`] = { value: 'used', validUntil: nonceValidUntil };
  }

  if (!(await setCookies(cookies))) {
    return [];
  }
  return [nonce, state, data];
};

const checkIsValidRedirect = (redirect: string | null) => {
  if (!redirect) {
    return false;
  }

  // do not redirect to current page or we go in circles
  if (redirect.startsWith(window.location.pathname)) {
    return false;
  }
  if (redirect.startsWith(`${window.location.origin}/${window.location.pathname}`)) {
    return false;
  }

  const dialogMatch = /^dialog:(\/[^@]*)/.exec(redirect);
  if (dialogMatch) {
    if (isSpecialPage(dialogMatch[1])) {
      return fischer.pages.myAccount().url;
    }
    return dialogMatch[1];
  }

  return redirect;
};

const handleLoginCallback = async () => {
  if (
    /(?:^#|&)brand=http/.test(window.location.hash) ||
    /(?:^\?|&)brand=http/.test(window.location.search)
  ) {
    const loginDialog: string | null = vueCookies.get(loginDialogCookies);
    if (!loginDialog) {
      console.error('no login dialog');
      return false;
    }
    const href = `${fischer.hostUrlBase()}${loginDialog}${window.location.search}${window.location.hash}`;
    logDebug('redirecting to dialog page', href);
    if (fischer.query('debug-no-redirect')) {
      console.debug('no redirect flag set');
    } else {
      window.location.href = href;
    }
    return true;
  }

  const hash = String(window.location.hash);
  logDebug('handling login callback', Boolean(hash));
  if (!hash) {
    logDebug('got no hash, not a login callback');
    return false;
  }

  const [, idToken] = /[#&]id_token=([^&]+)/.exec(hash) || [];
  const [, accessToken] = /[#&]access_token=([^&]+)/.exec(hash) || [];
  const [, stateId] = new RegExp(`[#&]state=([${allowedStateChars}]+)`).exec(hash) || [];
  // Unfortunately we can NOT remove the data from the hash, because
  // something causes the component to re-render

  if (!stateId) {
    console.error('state not found in hash');
    return false;
  }

  if (stateId === 'retry') {
    logDebug('got request to retry login - most likely bookmarked login page');
    return false;
  }

  const [nonce, state] = await restoreTokenState(stateId);
  if (nonce === 'used') {
    console.info('Nonce for state already used, re-attempting login');
    return false;
  }
  if (!state) {
    criticalLoginFailure('Could not restore state', state);
    return true;
  }
  if (isNonceError(idToken, nonce)) {
    criticalLoginFailure('Nonce failed');
    return true;
  }

  const { redirect, cartId } = state;

  let loginSuccessPage;
  clearHandlingFeeLimitCookie();
  vueCookies.remove(usernameCookie);
  if (IS_SHOWROOM) {
    setDevToken(accessToken, idToken);
    loginSuccessPage = '/';
  } else {
    const cartMergePromise = mergeCarts(accessToken, idToken, cartId);
    try {
      logDebug('posting token to sitecore');
      loginSuccessPage = await postAccessTokenInSession(accessToken, idToken);
    } catch {
      logDebug('error occured when posting to sitecore, not redirecting');
      return true;
    }
    await cartMergePromise;
  }

  notifyLogin();

  markAsNotIDS();
  logDebug('redirecting to login-success-page', redirect, checkIsValidRedirect(redirect));

  const data = /@(.*)/.exec(redirect || '')?.[1];

  emit('logged-in', {
    redirect: checkIsValidRedirect(redirect) || checkIsValidRedirect(loginSuccessPage) || '/',
    accessToken,
    data,
  });
  return true;
};

onMounted(async () => {
  try {
    if (IS_SSR) {
      return;
    }

    if (!(await handleLoginCallback())) {
      initializeAuthorization(fischer.isLoginDialogEnabled(), undefined, props.autodetect);
    }
  } catch (error) {
    console.error('error in created hook', error);
  }
});
</script>

<style lang="scss" scoped>
@import './site-login-standalone';
</style>
